/**
 * AwsRestService — implements IDataService against the Phase 2 API.
 *
 * All paths are scoped to /leagues/{leagueId}/*.
 * Every request carries an Authorization: Bearer token supplied via a
 * getToken() callback so AuthContext can silently refresh it before expiry.
 * On a 401 response the service calls getToken() once and retries.
 */

import type { IDataService, League, Player, GameRecord, Session } from '../../shared/types';

type Listener<T> = (value: T) => void;

interface State {
  league:  League | null;
  session: Session | null;
  players: Player[];
  games:   GameRecord[];
}

interface ServiceConfig {
  apiUrl:   string;
  leagueId: string;
  getToken: () => Promise<string>;
}

const POLL_INTERVAL_MS = 3000;

export class AwsRestService implements IDataService {
  private readonly apiUrl:   string;
  private readonly leagueId: string;
  private readonly getToken: () => Promise<string>;

  private leagueListeners:  Set<Listener<League | null>>  = new Set();
  private playersListeners: Set<Listener<Player[]>>       = new Set();
  private gamesListeners:   Set<Listener<GameRecord[]>>   = new Set();
  private sessionListeners: Set<Listener<Session | null>> = new Set();

  private lastState: State = { league: null, session: null, players: [], games: [] };
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private destroyed = false;

  constructor(config: ServiceConfig) {
    this.apiUrl   = config.apiUrl.replace(/\/$/, '');
    this.leagueId = config.leagueId;
    this.getToken = config.getToken;
    this.startPolling();
  }

  // ── Polling ──────────────────────────��─────────────────────────────────────

  private startPolling() {
    this.poll();
    this.pollTimer = setInterval(() => this.poll(), POLL_INTERVAL_MS);
  }

  private async poll() {
    if (this.destroyed) return;
    try {
      const data = await this.get<State>(`/leagues/${this.leagueId}/state`);
      this.emit(data);
    } catch {
      // Network or auth error — wait for next tick
    }
  }

  private emit(next: State) {
    if (JSON.stringify(next.league)  !== JSON.stringify(this.lastState.league))
      this.leagueListeners.forEach(cb => cb(next.league));
    if (JSON.stringify(next.session) !== JSON.stringify(this.lastState.session))
      this.sessionListeners.forEach(cb => cb(next.session));
    if (JSON.stringify(next.players) !== JSON.stringify(this.lastState.players))
      this.playersListeners.forEach(cb => cb(next.players));
    if (JSON.stringify(next.games)   !== JSON.stringify(this.lastState.games))
      this.gamesListeners.forEach(cb => cb(next.games));
    this.lastState = next;
  }

  // ── HTTP helpers ───────────────────────────────────────────────────────────

  private async authHeaders(): Promise<Record<string, string>> {
    const token = await this.getToken();
    return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
  }

  private async get<T>(path: string): Promise<T> {
    const headers = await this.authHeaders();
    let res = await fetch(`${this.apiUrl}${path}`, { headers });
    if (res.status === 401) {
      // Token may have just expired — getToken() will refresh it
      const retryHeaders = await this.authHeaders();
      res = await fetch(`${this.apiUrl}${path}`, { headers: retryHeaders });
    }
    if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
    return res.json();
  }

  private async put(path: string, body: unknown): Promise<void> {
    const headers = await this.authHeaders();
    let res = await fetch(`${this.apiUrl}${path}`, {
      method: 'PUT', headers,
      body: body === null ? 'null' : JSON.stringify(body),
    });
    if (res.status === 401) {
      const retryHeaders = await this.authHeaders();
      res = await fetch(`${this.apiUrl}${path}`, {
        method: 'PUT', headers: retryHeaders,
        body: body === null ? 'null' : JSON.stringify(body),
      });
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(body.error ?? `PUT ${path} failed: ${res.status}`);
    }
    await this.poll();
  }

  private async post(path: string, body: unknown): Promise<void> {
    const headers = await this.authHeaders();
    let res = await fetch(`${this.apiUrl}${path}`, {
      method: 'POST', headers, body: JSON.stringify(body),
    });
    if (res.status === 401) {
      const retryHeaders = await this.authHeaders();
      res = await fetch(`${this.apiUrl}${path}`, {
        method: 'POST', headers: retryHeaders, body: JSON.stringify(body),
      });
    }
    if (!res.ok) {
      const data = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(data.error ?? `POST ${path} failed: ${res.status}`);
    }
    await this.poll();
  }

  private async del(path: string): Promise<void> {
    const headers = await this.authHeaders();
    let res = await fetch(`${this.apiUrl}${path}`, { method: 'DELETE', headers });
    if (res.status === 401) {
      const retryHeaders = await this.authHeaders();
      res = await fetch(`${this.apiUrl}${path}`, { method: 'DELETE', headers: retryHeaders });
    }
    if (!res.ok) throw new Error(`DELETE ${path} failed: ${res.status}`);
    await this.poll();
  }

  private league_path(suffix: string) {
    return `/leagues/${this.leagueId}${suffix}`;
  }

  // ── IDataService subscriptions ────────────────────────────────────────────

  onLeague(cb: Listener<League | null>): () => void {
    cb(this.lastState.league);
    this.leagueListeners.add(cb);
    return () => this.leagueListeners.delete(cb);
  }

  onPlayers(cb: Listener<Player[]>): () => void {
    cb(this.lastState.players);
    this.playersListeners.add(cb);
    return () => this.playersListeners.delete(cb);
  }

  onGames(cb: Listener<GameRecord[]>): () => void {
    cb(this.lastState.games);
    this.gamesListeners.add(cb);
    return () => this.gamesListeners.delete(cb);
  }

  onSession(cb: Listener<Session | null>): () => void {
    cb(this.lastState.session);
    this.sessionListeners.add(cb);
    return () => this.sessionListeners.delete(cb);
  }

  // ── IDataService writes ────────────────────────────��──────────────────────

  async setLeague(league: League): Promise<void> {
    await this.put(this.league_path('/league'), league);
  }

  async addPlayer(player: Player): Promise<void> {
    await this.post(this.league_path('/players'), player);
  }

  async removePlayer(playerId: string): Promise<void> {
    await this.del(this.league_path(`/players/${playerId}`));
  }

  async addGame(game: GameRecord): Promise<void> {
    await this.post(this.league_path('/games'), game);
  }

  async updateGame(game: GameRecord): Promise<void> {
    await this.put(this.league_path(`/games/${game.id}`), game);
  }

  async setSession(session: Session | null): Promise<void> {
    await this.put(this.league_path('/session'), session);
  }

  destroy(): void {
    this.destroyed = true;
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }
}
